import { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Avatar,
    Divider,
    Grid,
} from '@mui/material';
import { Person, Email, CalendarToday, Edit, Save, Cancel } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';

const Profile = () => {
    const { updateUser } = useAuth();
    const [profileData, setProfileData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        createdAt: '',
    });
    const [originalData, setOriginalData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        createdAt: '',
    });
    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            setLoading(true);
            const response = await authAPI.getProfile();
            const userData = response.data.user;

            const data = {
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                email: userData.email || '',
                createdAt: userData.createdAt || '',
            };

            setProfileData(data);
            setOriginalData(data);
        } catch (error) {
            console.error('Error fetching profile:', error);
            setError('Failed to load profile data');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setProfileData({
            ...profileData,
            [e.target.name]: e.target.value,
        });
    };

    const handleEdit = () => {
        setEditMode(true);
        setError('');
        setSuccess('');
    };

    const handleCancel = () => {
        setEditMode(false);
        // Reset to original data
        setProfileData(originalData);
        setError('');
        setSuccess('');
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError('');

            const updateData = {
                firstName: profileData.firstName,
                lastName: profileData.lastName,
                email: profileData.email,
            };

            await authAPI.updateProfile(updateData);

            // Update the user context
            updateUser(updateData);

            // Update original data to reflect the saved changes
            setOriginalData({
                ...originalData,
                firstName: profileData.firstName,
                lastName: profileData.lastName,
                email: profileData.email,
            });

            setEditMode(false);
            setSuccess('Profile updated successfully!');
        } catch (error) {
            console.error('Error updating profile:', error);
            setError(error.response?.data?.message || 'Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    if (loading) {
        return (
            <Container maxWidth="md">
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="md">
            <Paper variant="outlined" sx={{ p: 4, mt: 2 }}>
                {/* Header */}
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                    <Box display="flex" alignItems="center">
                        <Avatar
                            sx={{
                                width: 64,
                                height: 64,
                                mr: 2,
                                bgcolor: 'primary.main',
                                fontSize: '1.5rem',
                            }}
                        >
                            {profileData.firstName?.charAt(0)}{profileData.lastName?.charAt(0)}
                        </Avatar>
                        <Box>
                            <Typography variant="h4" component="h1" gutterBottom>
                                User Profile
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Manage your account information
                            </Typography>
                        </Box>
                    </Box>

                    {!editMode ? (
                        <Button
                            variant="outlined"
                            startIcon={<Edit />}
                            onClick={handleEdit}
                        >
                            Edit Profile
                        </Button>
                    ) : (
                        <Box>
                            <Button
                                variant="outlined"
                                startIcon={<Cancel />}
                                onClick={handleCancel}
                                sx={{ mr: 1 }}
                                disabled={saving}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={saving ? <CircularProgress size={16} /> : <Save />}
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </Box>
                    )}
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Alerts */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                {/* Profile Form */}
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="First Name"
                            name="firstName"
                            value={profileData.firstName}
                            onChange={handleChange}
                            disabled={!editMode || saving}
                            InputProps={{
                                startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                            variant="outlined"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Last Name"
                            name="lastName"
                            value={profileData.lastName}
                            onChange={handleChange}
                            disabled={!editMode || saving}
                            InputProps={{
                                startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                            variant="outlined"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Email Address"
                            name="email"
                            type="email"
                            value={profileData.email}
                            disabled
                            //   onChange={handleChange}
                            //   disabled={!editMode || saving}
                            InputProps={{
                                startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                            variant="outlined"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Account Created"
                            value={formatDate(profileData.createdAt)}
                            disabled
                            InputProps={{
                                startAdornment: <CalendarToday sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                            variant="outlined"
                            helperText="This is when your account was first created"
                        />
                    </Grid>
                </Grid>

                {/* Additional Info */}
                <Box mt={4}>
                    <Typography variant="h6" gutterBottom>
                        Account Information
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Your account was created on {formatDate(profileData.createdAt)}.
                        You can update your first name, last name, and email address above.
                    </Typography>
                </Box>
            </Paper>
        </Container>
    );
};

export default Profile;